import { MapAlt as Map } from '@styled-icons/boxicons-regular'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components/macro'

import { tokenizeQuery } from '../../utils/tokenize'
import { MultiSelect } from '../ui/MultiSelect'
import {
  CountBadge,
  DetailsBlock,
  ItemWrapper,
  PrimaryName,
  SecondaryDetails,
  TypeNameOption,
} from '../ui/SelectOptionLayout'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 10px;
  margin-bottom: 20px;
  width: 100%;
  max-width: 300px;
`

const CategoryLabel = styled.div`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.tertiaryText};
  margin-bottom: 5px;
`

// Custom component to display city with count
const PlaceOption = ({ place, count }) => {
  if (place.isCoordinates) {
    return (
      <ItemWrapper>
        <DetailsBlock>
          <PrimaryName>
            <Map
              style={{ verticalAlign: 'sub', marginRight: '0.1em' }}
              size="1em"
            />
            {place.coordinatesGrid}
          </PrimaryName>
        </DetailsBlock>
        {count !== undefined && <CountBadge>{count}</CountBadge>}
      </ItemWrapper>
    )
  }

  const locationDetails = [place.state, place.country]
    .filter(Boolean)
    .join(', ')

  return (
    <ItemWrapper>
      <DetailsBlock>
        <PrimaryName>{place.city}</PrimaryName>
        {locationDetails && (
          <SecondaryDetails>{locationDetails}</SecondaryDetails>
        )}
      </DetailsBlock>
      {count !== undefined && <CountBadge>{count}</CountBadge>}
    </ItemWrapper>
  )
}

// Filter function for select options using tokenized search
const filterOption = (candidate, input) => {
  if (!input) {
    return true
  }

  const tokenizedInput = tokenizeQuery(input)
  const searchReference = candidate.data.searchReference

  return searchReference && searchReference.includes(tokenizedInput)
}

const filterTypeOption = (candidate, input) => {
  if (!input) {
    return true
  }

  return candidate.data.type
    .searchReference({ includeSynonyms: false })
    .includes(tokenizeQuery(input))
}

const TypesAndPlaces = ({
  typeCounts,
  cityCounts,
  selectedTypes,
  selectedPlaces,
  onTypeChange,
  onPlaceChange,
  isDisabled = false,
}) => {
  const { t } = useTranslation()

  return (
    <Container>
      <div>
        <CategoryLabel>{t('glossary.type.other')}</CategoryLabel>
        <MultiSelect
          options={typeCounts}
          getOptionValue={(option) => option.type.id}
          value={
            selectedTypes
              ?.map((typeId) =>
                typeCounts.find((option) => option.type.id === typeId),
              )
              .filter(Boolean) || []
          }
          onChange={(options) =>
            onTypeChange(options?.map((option) => option.type.id) || [])
          }
          placeholder={t('glossary.type.one')}
          isClearable
          isDisabled={isDisabled}
          formatOptionLabel={(option, { context }) => (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <TypeNameOption
                type={option.type}
                showSynonyms={false}
                count={
                  context === 'menu'
                    ? option.filteredCount !== undefined &&
                      option.filteredCount !== option.count
                      ? `${option.filteredCount}/${option.count}`
                      : option.count
                    : undefined
                }
              />
            </div>
          )}
          filterOption={filterTypeOption}
        />
      </div>

      <div>
        <CategoryLabel>{t('pages.changes.place.other')}</CategoryLabel>
        <MultiSelect
          options={cityCounts}
          value={
            selectedPlaces
              ?.map((place) =>
                cityCounts.find((option) => option.value === place),
              )
              .filter(Boolean) || []
          }
          onChange={(options) =>
            onPlaceChange(options?.map((option) => option.value) || [])
          }
          placeholder={t('pages.changes.place.one')}
          isClearable
          isDisabled={isDisabled}
          formatOptionLabel={(option, { context }) => (
            <PlaceOption
              place={option}
              count={
                context === 'menu'
                  ? option.filteredCount !== undefined &&
                    option.filteredCount !== option.count
                    ? `${option.filteredCount}/${option.count}`
                    : option.count
                  : undefined
              }
            />
          )}
          filterOption={filterOption}
        />
      </div>
    </Container>
  )
}

export default TypesAndPlaces
